import * as sentry from '@sentry/react';
import {call, put, select, takeLatest} from 'redux-saga/effects';

import i18n from 'config/i18n/i18n';
import {
  getBasketCalculation,
  getBasketPromotionsLite,
  updateBasketItemPreview,
  changeBasketQuantity,
  getMetaDataV5,
  updateMetaDataV5
} from 'services/basket/basket-service';
import SendBrowserEvent from 'services/send-browser-event';
import {setCurrentStoreAction} from 'store/actions/app-action';
import {
  getBasketCalculationAction,
  getBasketPromotionsLiteAction,
  removeUnavailableItemsAction,
  setBasketDetailsAction,
  setBasketPromotionsLiteAction,
  setIsBasketApiFailedAction,
  updatePreviewItemQuantityAction,
  deleteSelectedItemsAction,
  setSelectedItemsAction,
  setBasketPromotionsLiteLoadingAction,
  batchUpdateItemsQuantityAction,
  bulkUpdateBasketItemsPreviewAction,
  getRM3PromotionsAction,
  setRM3PromotionsAction,
  getMetaDataAction,
  updateMetaDataAction
} from 'store/actions/basket-action';
import {AppMode} from 'store/types/app';
import {AppState} from 'store/types/app';
import {AuthState} from 'store/types/auth';
import {
  BasketCalculation,
  BasketItemInput,
  BasketRoot,
  GetMetaDataPayload,
  ItemToUpdateQuantity,
  MetaDataResponse,
  PromoAdvice,
  ReduxBasketState,
  TCalculateBasketPayload,
  UpdateMetaDataPayload
} from 'types/basket';
import {
  findBasketItemByVariantId,
  getIdsFromBasketItems,
  getItemDetailsForPromotions,
  getPreviewItemsWithBasketItems
} from 'utils';
import {extractStoreInfoData, isSyncBasketEnabled} from 'utils/helper';

import {getRmPromotions} from '../../services/loyalty/loyalty-service';

function* getBasketCalculationsSaga(action?: {payload?: BasketCalculation}): Generator {
  try {
    const payload = action?.payload;
    const lang = i18n.language;
    const isSyncBasket = isSyncBasketEnabled();
    const selectedBasketItems = isSyncBasket
      ? yield select((state) => state.basket.selectedItems)
      : JSON.parse(localStorage.getItem('selectedItems') || '[]') || [];
    yield put(setIsBasketApiFailedAction(false));
    const params: any = yield select((state) => {
      const {currentStore, defaultMembershipCard, appMode} = state?.app || {};
      const idToken = state.auth?.idToken;
      const customerId = state.auth?.customerId;

      const selectedItems = isSyncBasket && payload?.isEmptyBasketPayload ? undefined : selectedBasketItems;
      return {
        makroMemberCard: defaultMembershipCard,
        zipCode: currentStore?.zipcode,
        subDistrict: currentStore?.subdistrict,
        lang,
        idToken,
        isExpress: appMode === AppMode.express,
        basketItems: selectedItems ? selectedItems : undefined,
        customerId
      };
    });
    const basketResult = yield call(getBasketCalculation, params as TCalculateBasketPayload);

    const basket = basketResult as BasketRoot;

    yield put(setBasketDetailsAction(basket.basket));
    yield put(getBasketPromotionsLiteAction());
    if (basket?.basket?.basketItems && basket?.basket?.basketItems.length) {
      if (isSyncBasket) {
        const selectedItems = basket.basket.basketItems.filter((item) => item?.productDetails?.isSelected) || [];
        const selectedItemIds = getIdsFromBasketItems(selectedItems);
        yield put(setSelectedItemsAction([...selectedItemIds]));
      } else {
        const allItemsInBasket = getIdsFromBasketItems(basket.basket.basketItems);
        const basketSelectedItems = allItemsInBasket.filter((productId: string) =>
          selectedBasketItems.includes(productId)
        );
        localStorage.setItem('selectedItems', JSON.stringify([...basketSelectedItems]));
      }
    }
    if (
      payload?.isBasketPreviewItemsUpdated &&
      basket?.basket?.unavailableItems &&
      basket?.basket?.basketItems &&
      !basket?.basket?.error?.hasError
    ) {
      const previewItems = getPreviewItemsWithBasketItems([
        ...(basket?.basket?.basketItems ?? []),
        ...(basket?.basket?.unavailableItems ?? [])
      ]);
      SendBrowserEvent.basketContentUpdated(previewItems);
    }
    if (basket?.basket?.error) {
      yield put(setIsBasketApiFailedAction(true));
    }
  } catch (e: any) {
    sentry.captureException(e);
    yield put(setIsBasketApiFailedAction(true));
  }
}

function* updateItemQuantityWithPreviewSaga(action: {payload: any}): Generator {
  try {
    const {basket} = (yield select((state) => state.basket) as unknown) as ReduxBasketState;
    const isSyncBasket = isSyncBasketEnabled();
    const selectedItems = isSyncBasket
      ? yield select((state) => state.basket.selectedItems)
      : JSON.parse(localStorage.getItem('selectedItems') || '[]') || [];

    const {idToken, productId, quantity, variantId} = action.payload;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;

    const basketItem = findBasketItemByVariantId(basket?.basketItems || [], variantId);
    const unavailableBasketItem = findBasketItemByVariantId(basket?.unavailableItems || [], variantId);

    const allBasketItems = [...(basket?.basketItems || []), ...(basket?.unavailableItems || [])];

    const isLastItem = allBasketItems.length === 1 && quantity === 0;
    if (quantity === 0) {
      if (basketItem) {
        //deleting from available items
        const newBasketItems = basket?.basketItems.filter((item) => item.productId !== basketItem?.productId);
        yield put(setBasketDetailsAction({...basket, basketItems: newBasketItems}));
      } else if (unavailableBasketItem) {
        //deleting from un-available items
        const newUnavailableItems = basket?.unavailableItems.filter(
          (item) => item.productId !== unavailableBasketItem?.productId
        );
        yield put(setBasketDetailsAction({...basket, unavailableItems: newUnavailableItems}));
      }
      if (selectedItems.includes(productId)) {
        const newSelectedItems = selectedItems.filter((itm: string) => itm !== productId);
        isSyncBasket
          ? put(setSelectedItemsAction(newSelectedItems))
          : localStorage.setItem('selectedItems', JSON.stringify(newSelectedItems || []));
      }
    }

    const updateBasketItem = yield call(
      () =>
        updateBasketItemPreview(
          [
            {
              productId,
              quantity
            }
          ],
          idToken || '',
          isExpress
        ) as any
    );
    const result = updateBasketItem as any;
    if (result?.data?.basketItems) {
      SendBrowserEvent.basketContentUpdated(result.data.basketItems);
      yield !isLastItem && put(getBasketCalculationAction());
    } else {
      if (result?.error?.code) {
        SendBrowserEvent.showErrorToast(result.error);
      }
      if (quantity === 0) {
        yield put(setBasketDetailsAction(basket));
      }
    }
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* removeUnavailableItemsSaga(action: {payload: (isOOSProductsDeleted?: boolean) => Promise<void>}): Generator {
  try {
    const {basket} = (yield select((state) => state.basket) as unknown) as ReduxBasketState;
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    const isSyncBasket = isSyncBasketEnabled();
    const selectedItems = isSyncBasket
      ? yield select((state) => state.basket.selectedItems)
      : JSON.parse(localStorage.getItem('selectedItems') || '[]') || [];
    const onCheckoutClick = action.payload;
    const idsToBeRemoved: string[] = [];
    const bulkDeleteItemsArray: BasketItemInput[] = basket?.unavailableItems.map((item) => {
      idsToBeRemoved.push(item?.productId);
      return {
        productId: item.productId,
        quantity: 0
      };
    }) as any;
    const newSelectedItems = selectedItems.filter((itm: string) => !idsToBeRemoved.includes(itm));
    yield call(() => getBasketCalculationsSaga());
    const updateBasketItem = yield call(
      () => updateBasketItemPreview(bulkDeleteItemsArray, idToken || '', isExpress) as any
    );
    const result = updateBasketItem as any;
    if (result?.data) {
      yield call(() => SendBrowserEvent.basketContentUpdated(result.data.basketItems));
      yield call(() => onCheckoutClick(true));
    }
    isSyncBasket
      ? put(setSelectedItemsAction(newSelectedItems))
      : localStorage.setItem('selectedItems', JSON.stringify(newSelectedItems));
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* deleteSelectedItemsSaga(): Generator {
  try {
    const isSyncBasket = isSyncBasketEnabled();
    const selectedItems = isSyncBasket
      ? yield select((state) => state.basket.selectedItems)
      : JSON.parse(localStorage.getItem('selectedItems') || '[]') || [];
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;

    const bulkDeleteItemsArray: BasketItemInput[] = selectedItems.map((id: string) => {
      return {
        productId: id,
        quantity: 0
      };
    }) as any;
    if (bulkDeleteItemsArray.length) {
      const updateBasketItem = yield call(
        () => updateBasketItemPreview(bulkDeleteItemsArray, idToken || '', isExpress) as any
      );
      const result = updateBasketItem as any;
      if (result?.data) {
        yield call(() => getBasketCalculationsSaga());
        yield call(() => SendBrowserEvent.basketContentUpdated(result.data.basketItems));
        yield put(setSelectedItemsAction([]));
        isSyncBasket ? put(setSelectedItemsAction([])) : localStorage.setItem('selectedItems', JSON.stringify([]));
      }
    }
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* getBasketPromotionsLiteSaga(): Generator {
  try {
    yield put(setBasketPromotionsLiteLoadingAction(true));
    const {defaultMembershipCard} = (yield select((state) => state?.app) as unknown) as AppState;
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const {basket} = (yield select((state) => state.basket) as unknown) as ReduxBasketState;
    const basketItemsCount = basket?.basketItems?.length;
    const unavailableBasketItemsCount = basket?.unavailableItems?.length ?? 0;

    if (defaultMembershipCard && basketItemsCount && basketItemsCount > unavailableBasketItemsCount) {
      const data = yield call(() => {
        const itemDetails = getItemDetailsForPromotions(basket?.basketItems, basket.basketItemsFromFe ?? [], true);
        if (itemDetails?.length) {
          return getBasketPromotionsLite(
            {
              storeId: basket?.storeCode ?? '',
              customerId: defaultMembershipCard?.cardNumber ?? '',
              itemDetails
            },
            idToken
          );
        }
      });

      yield put(setBasketPromotionsLiteAction(data));
    }
  } catch (e: any) {
    yield put(
      setBasketPromotionsLiteAction({
        skuBasedPromotions: [],
        promotionSuggestions: [],
        estimatedPointsReceive: 0,
        estimatedPointsDetails: []
      })
    );
    sentry.captureException(e);
  } finally {
    yield put(setBasketPromotionsLiteLoadingAction(false));
  }
}

function* getRM3PromotionsSaga(action: {payload: string[]}): Generator {
  try {
    const {rm3Promotions: currentPromotion} = (yield select((state) => state.basket) as unknown) as ReduxBasketState;

    const filteredPayload = action.payload.filter((id) => !currentPromotion[id]);

    if (filteredPayload.length === 0) {
      return;
    }

    const template = filteredPayload.reduce(
      (acc, id) => ({
        ...acc,
        [id]: {promoAdvice: []}
      }),
      {} as Record<string, {promoAdvice: PromoAdvice[]}>
    );

    yield put(setRM3PromotionsAction(template));

    const {currentStore} = (yield select((state) => state.app)) as AppState;
    const storeCode = currentStore?.storeCodes ?? [];

    const response = yield call(() => getRmPromotions(filteredPayload, storeCode));

    yield put(setRM3PromotionsAction(response as Record<string, {promoAdvice: PromoAdvice[]}>));
  } catch (e) {
    sentry.captureException(e);
  }
}

function* batchUpdateItemsQuantitySaga(action: {payload: ItemToUpdateQuantity[]}): Generator {
  try {
    const itemsToUpdate = action.payload;
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const {basket} = (yield select((state) => state.basket) as unknown) as ReduxBasketState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    if (!basket) return;
    const basketToUpdate = {...basket}; // ensure no mutiation of original basket object

    for (const itemToUpdate of itemsToUpdate) {
      const items = changeBasketQuantity(basketToUpdate, {
        quantity: itemToUpdate.quantity,
        variantId: itemToUpdate.variantId
      });
      basketToUpdate.basketItems = items;
    }

    const updated = yield call(
      () => updateBasketItemPreview(basketToUpdate.basketItems || [], idToken || '', isExpress) as any
    );
    if (updated) yield call(() => getBasketCalculationsSaga());
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* bulkUpdateBasketItemsPreviewSaga(action: {payload: BasketItemInput[]}): Generator {
  try {
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    const itemsToUpdate = action.payload;
    const updateBasketItem = yield call(
      () => updateBasketItemPreview(itemsToUpdate || [], idToken || '', isExpress) as any
    );
    const result = updateBasketItem as any;
    if (result?.data) {
      yield call(() => getBasketCalculationsSaga());
      yield call(() => SendBrowserEvent.basketContentUpdated(result.data.basketItems));
    }
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* updateMetaDataSaga(action: {payload: UpdateMetaDataPayload}): Generator {
  try {
    const {address, storeInfo} = action.payload;
    const idToken = (yield select((state) => state.auth?.idToken) as unknown) as string;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    if (!idToken) return;

    const response = (yield call(
      updateMetaDataV5,
      storeInfo?.zipcode || '',
      storeInfo?.subdistrict || '',
      idToken || '',
      storeInfo?.stores || [],
      address?.id || '',
      address
        ? {
            address,
            subdistrictEn: address.subDistrict?.nameEn,
            subdistrictTh: address.subDistrict?.nameTh,
            subdistrictId: address.subDistrict?.id
          }
        : {},
      isExpress
    )) as MetaDataResponse;

    if (response?.status === 'SUCCESS') {
      const {isSameDayDelivery, showStorePickup, storeCodes} = extractStoreInfoData(response);
      yield put(
        setCurrentStoreAction({
          zipcode: response?.zipCode,
          subdistrict: response?.subDistrict,
          storeCodes: storeCodes,
          showStorePickup: showStorePickup,
          isSameDayDelivery: isSameDayDelivery
        })
      );
      yield put(getBasketCalculationAction());
    }
  } catch (e: any) {
    console.error('Error at updateMetaDataSaga v5:', e);
    sentry.captureException(e);
  }
}

function* getMetaDataSaga(action: {payload: GetMetaDataPayload | undefined}): Generator {
  try {
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    if (!idToken) return;

    const response = (yield call(getMetaDataV5, idToken, isExpress) as unknown) as MetaDataResponse;

    if (response?.status === 'SUCCESS') {
      const {isSameDayDelivery, showStorePickup, storeCodes} = extractStoreInfoData(response);
      yield put(
        setCurrentStoreAction({
          zipcode: response?.zipCode,
          subdistrict: response?.subDistrict,
          storeCodes: storeCodes,
          showStorePickup: showStorePickup,
          isSameDayDelivery: isSameDayDelivery
        })
      );

      if (action?.payload?.callCalculate) {
        yield put(getBasketCalculationAction({isBasketPreviewItemsUpdated: true, isEmptyBasketPayload: true}));
      }
    }
  } catch (e: any) {
    console.error('Error at getMetaDataSaga v5:', e);
    sentry.captureException(e);
  }
}

export default function* () {
  yield takeLatest(getBasketCalculationAction, getBasketCalculationsSaga);
  yield takeLatest(updatePreviewItemQuantityAction, updateItemQuantityWithPreviewSaga);
  yield takeLatest(removeUnavailableItemsAction, removeUnavailableItemsSaga);
  yield takeLatest(deleteSelectedItemsAction, deleteSelectedItemsSaga);
  yield takeLatest(getBasketPromotionsLiteAction, getBasketPromotionsLiteSaga);
  yield takeLatest(getRM3PromotionsAction, getRM3PromotionsSaga);
  yield takeLatest(batchUpdateItemsQuantityAction, batchUpdateItemsQuantitySaga);
  yield takeLatest(bulkUpdateBasketItemsPreviewAction, bulkUpdateBasketItemsPreviewSaga);
  yield takeLatest(updateMetaDataAction, updateMetaDataSaga);
  yield takeLatest(getMetaDataAction, getMetaDataSaga);
}
